import { Button, DatePicker, Form, Input, Modal, Select, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { IBusinessFull } from "../../../../interfaces/businessFull.interface";
import {
  editBusinessThunk,
  selectCurrentBusiness,
} from "../../../businesses/businessesSlice";
import { selectSalesReps } from "../../SalesSlice";
import { LocalDateAsUTC } from "../../../../utils/LocalDateAsUTC";
import dayjs from "dayjs";
import { selectIsKlearlySalesSuperAdmin } from "../../../access-control/accountSlice";
import SalesRepIcon from "../Sales-Rep-Icon/SalesRepIcon";

interface IPropsBusinessEditorModel {
  visible: boolean;
  setVisible: any;
}

const salesFormFieldLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 10 },
};
const dateFormat = "DD/MM/YYYY";
export default function SalesBusinessEditorModal({
  visible,
  setVisible,
}: IPropsBusinessEditorModel) {
  const [loading, setLoading] = useState(false);
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const salesReps = useAppSelector(selectSalesReps);
  const isSalesSuperAdmin = useAppSelector(selectIsKlearlySalesSuperAdmin);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [saleRepSelectionOptions, setSaleRepSelectionOptions] = useState([
    ...(isSalesSuperAdmin ? [{ value: "", label: "None" }] : []),
    ...salesReps,
  ]);

  // Update options when salesReps or isSalesSuperAdmin changes
  useEffect(() => {
    setSaleRepSelectionOptions([
      ...(isSalesSuperAdmin ? [{ value: "", label: "None" }] : []),
      ...salesReps,
    ]);
  }, [salesReps, isSalesSuperAdmin]);

  // Reset form when modal becomes visible
  useEffect(() => {
    if (visible && currentBusiness) {
      form.resetFields();
    }
  }, [visible, currentBusiness, form]);

  useEffect(() => {
    if (visible && currentBusiness) {
      // console.log("Setting form values:", {
      //   salesRepresentative: currentBusiness.salesRepresentative,
      //   salesRepresentativeExpDate: currentBusiness.salesRepresentativeExpDate,
      //   salesRepresentative2: currentBusiness.salesRepresentative2,
      //   salesRepresentative2ExpDate:
      //     currentBusiness.salesRepresentative2ExpDate,
      //   salesBusinessDeveloper: currentBusiness.salesBusinessDeveloper,
      //   salesBusinessDeveloperExpDate:
      //     currentBusiness.salesBusinessDeveloperExpDate,
      //   directSalesLead: currentBusiness.directSalesLead,
      //   directSalesLeadExpDate: currentBusiness.directSalesLeadExpDate,
      //   customerSuccessManager: currentBusiness.customerSuccessManager,
      // });

      form.setFieldsValue({
        salesRepresentative: currentBusiness.salesRepresentative,
        salesRepresentativeExpDate: currentBusiness.salesRepresentativeExpDate
          ? dayjs(currentBusiness.salesRepresentativeExpDate)
          : null,
        salesRepresentative2: currentBusiness.salesRepresentative2,
        salesRepresentative2ExpDate: currentBusiness.salesRepresentative2ExpDate
          ? dayjs(currentBusiness.salesRepresentative2ExpDate)
          : null,
        salesBusinessDeveloper: currentBusiness.salesBusinessDeveloper,
        salesBusinessDeveloperExpDate:
          currentBusiness.salesBusinessDeveloperExpDate
            ? dayjs(currentBusiness.salesBusinessDeveloperExpDate)
            : null,
        directSalesLead: currentBusiness.directSalesLead,
        directSalesLeadExpDate: currentBusiness.directSalesLeadExpDate
          ? dayjs(currentBusiness.directSalesLeadExpDate)
          : null,
        teamManager: currentBusiness.teamManager,
        teamManagerExpDate: currentBusiness.teamManagerExpDate
          ? dayjs(currentBusiness.teamManagerExpDate)
          : null,
        customerSuccessManager: currentBusiness.customerSuccessManager,
        partnerManager: currentBusiness.partnerManager,
        partnerManagerExpDate: currentBusiness.partnerManagerExpDate
          ? dayjs(currentBusiness.partnerManagerExpDate)
          : null,
      });
    }

    // setSaleRepSelectionOptions(
    //   getFilteredSalesReps([
    //     currentBusiness.salesRepresentative,
    //     currentBusiness.salesRepresentative2,
    //     currentBusiness.salesBusinessDeveloper,
    //   ])
    // );

    return () => {
      setSaleRepSelectionOptions([
        ...(isSalesSuperAdmin ? [{ value: "", label: "None" }] : []),
        ...salesReps,
      ]);
    };
  }, [currentBusiness, form, visible]);

  const getExpirationDate = (salesRep: string, firstTsxDate: string) => {
    console.log("getExpirationDate called with:", { salesRep, firstTsxDate });

    // Find the sales rep to see their configured months
    const salesRepData = salesReps.find((r) => r.value === salesRep);
    console.log("salesRepData:", salesRepData);

    // For bonus period, always use 12 months regardless of the sales rep's configured months
    let monthsToExp = 12;
    console.log("monthsToExp (bonus period):", monthsToExp);

    const result = new LocalDateAsUTC(firstTsxDate).addMonths(monthsToExp).ISO;
    console.log("calculated expiration date:", result);
    return result;
  };
  const handleSubmit = async (values: Partial<IBusinessFull>) => {
    setLoading(true);
    console.log("values", values);

    // Create updated roles by merging current business data with form values
    const updatedRoles = {
      salesRepresentative: currentBusiness?.salesRepresentative,
      salesRepresentative2: currentBusiness?.salesRepresentative2,
      salesBusinessDeveloper: currentBusiness?.salesBusinessDeveloper,
      directSalesLead: currentBusiness?.directSalesLead,
      teamManager: currentBusiness?.teamManager,
      customerSuccessManager: currentBusiness?.customerSuccessManager,
      partnerManager: currentBusiness?.partnerManager,
      // Override with form values if they exist
      ...values,
    };

    console.log("updatedRoles", updatedRoles);

    // Count sales roles (AE + BDR) - exclude CSM and Direct Sales Lead from this count
    const salesRoles = [
      updatedRoles.salesRepresentative,
      updatedRoles.salesRepresentative2,
      updatedRoles.salesBusinessDeveloper,
    ].filter(Boolean);

    // Check for duplicate sales reps (excluding CSM - CSM can be assigned to multiple roles)
    const nonCSMRoles = [
      updatedRoles.salesRepresentative,
      updatedRoles.salesRepresentative2,
      updatedRoles.salesBusinessDeveloper,
      updatedRoles.directSalesLead,
      updatedRoles.teamManager,
      updatedRoles.partnerManager,
    ].filter(Boolean);

    const uniqueNonCSMRoles = new Set(nonCSMRoles);

    if (nonCSMRoles.length !== uniqueNonCSMRoles.size) {
      // Set error in form for duplicate selection (only for non-CSM roles)
      form.setFields([
        {
          name: "salesRepresentative",
          errors: ["Duplicate Sales Reps selected"],
        },
        {
          name: "salesRepresentative2",
          errors: ["Duplicate Sales Reps selected"],
        },
        {
          name: "salesBusinessDeveloper",
          errors: ["Duplicate Sales Reps selected"],
        },
        {
          name: "directSalesLead",
          errors: ["Duplicate Sales Reps selected"],
        },
        {
          name: "teamManager",
          errors: ["Duplicate Sales Reps selected"],
        },
        {
          name: "partnerManager",
          errors: ["Duplicate Sales Reps selected"],
        },
      ]);
      setLoading(false);
      return;
    }

    // Business logic: Allow up to 3 sales roles (2x AE + 1x BDR) per deal
    // ✅ BDR + AE (with or without CSM/Direct Sales Lead)
    // ✅ 2x AE (with or without CSM/Direct Sales Lead)
    // ✅ 1x BDR + 2x AE

    if (salesRoles.length > 3) {
      // More than 3 sales roles selected
      form.setFields([
        {
          name: "salesRepresentative",
          errors: ["Maximum 3 sales roles allowed per deal"],
        },
        {
          name: "salesRepresentative2",
          errors: ["Maximum 3 sales roles allowed per deal"],
        },
        {
          name: "salesBusinessDeveloper",
          errors: ["Maximum 3 sales roles allowed per deal"],
        },
      ]);
      setLoading(false);
      return;
    }

    // Clear any previous errors
    form.setFields([
      {
        name: "salesRepresentative",
        errors: [],
      },
      {
        name: "salesRepresentative2",
        errors: [],
      },
      {
        name: "salesBusinessDeveloper",
        errors: [],
      },
      {
        name: "directSalesLead",
        errors: [],
      },
      {
        name: "teamManager",
        errors: [],
      },
      {
        name: "customerSuccessManager",
        errors: [],
      },
      {
        name: "partnerManager",
        errors: [],
      },
    ]);

    // Set default expiration dates based on first transaction date (only if firstTsxDate exists)
    console.log("=== EXPIRATION DATE LOGIC ===");
    console.log("currentBusiness.firstTsxDate:", currentBusiness.firstTsxDate);
    console.log(
      "currentBusiness.firstTsxDate type:",
      typeof currentBusiness.firstTsxDate
    );

    if (currentBusiness.firstTsxDate) {
      const baseDate =
        typeof currentBusiness.firstTsxDate === "string"
          ? currentBusiness.firstTsxDate
          : currentBusiness.firstTsxDate.toISOString();

      console.log("baseDate:", baseDate);
      console.log("updatedRoles:", updatedRoles);

      if (
        updatedRoles.salesRepresentative &&
        !currentBusiness?.salesRepresentativeExpDate
      ) {
        updatedRoles.salesRepresentativeExpDate = getExpirationDate(
          updatedRoles.salesRepresentative,
          baseDate
        );
      }
      if (
        updatedRoles.salesRepresentative2 &&
        !currentBusiness?.salesRepresentative2ExpDate
      ) {
        updatedRoles.salesRepresentative2ExpDate = getExpirationDate(
          updatedRoles.salesRepresentative2,
          baseDate
        );
      }
      if (
        updatedRoles.salesBusinessDeveloper &&
        !currentBusiness?.salesBusinessDeveloperExpDate
      ) {
        updatedRoles.salesBusinessDeveloperExpDate = getExpirationDate(
          updatedRoles.salesBusinessDeveloper,
          baseDate
        );
      }
      if (
        updatedRoles.directSalesLead &&
        !currentBusiness?.directSalesLeadExpDate
      ) {
        updatedRoles.directSalesLeadExpDate = getExpirationDate(
          updatedRoles.directSalesLead,
          baseDate
        );
      }
      if (updatedRoles.teamManager && !currentBusiness?.teamManagerExpDate) {
        updatedRoles.teamManagerExpDate = getExpirationDate(
          updatedRoles.teamManager,
          baseDate
        );
      }
      if (
        updatedRoles.partnerManager &&
        !currentBusiness?.partnerManagerExpDate
      ) {
        updatedRoles.partnerManagerExpDate = getExpirationDate(
          updatedRoles.partnerManager,
          baseDate
        );
      }
    } else {
      console.log("No firstTsxDate found, skipping expiration date logic");
    }

    // return;
    await dispatch(
      editBusinessThunk({
        _id: currentBusiness._id,
        updatedFields: updatedRoles,
      })
    );

    setLoading(false);
    setVisible(false);
  };
  const handleCancel = () => {
    setVisible(false);
  };

  type SalesRepType =
    | "salesRepresentative"
    | "salesRepresentative2"
    | "salesBusinessDeveloper"
    | "directSalesLead"
    | "teamManager"
    | "customerSuccessManager"
    | "partnerManager";

  const handleSelectionChange = (value: string, salesRep: SalesRepType) => {
    console.log("handleSelectionChange:", { value, salesRep });

    // If a sales rep is selected and there's no existing expiration date, set the default
    if (value && currentBusiness?.firstTsxDate) {
      const baseDate =
        typeof currentBusiness.firstTsxDate === "string"
          ? currentBusiness.firstTsxDate
          : currentBusiness.firstTsxDate.toISOString();

      // Get the expiration field name for this sales rep
      const expirationFieldName = SalesRepsForms.find(
        (form) => form.salesRepFieldName === salesRep
      )?.salesRepExpFieldName;

      if (expirationFieldName) {
        // Check if there's no existing expiration date for this role
        const currentExpDate =
          currentBusiness[expirationFieldName as keyof IBusinessFull];

        if (!currentExpDate) {
          console.log("Setting default expiration date for", salesRep);
          const defaultExpDate = getExpirationDate(value, baseDate);
          console.log("Default expiration date:", defaultExpDate);

          // Set the form field value
          form.setFieldValue(expirationFieldName, dayjs(defaultExpDate));
        }
      }
    }
  };

  const SalesRepsForms: {
    title: string;
    salesRepFieldName: SalesRepType;
    salesRepExpFieldName: string;
    hasExpiration?: boolean;
  }[] = [
    {
      title: "Account Executive 1",
      salesRepFieldName: "salesRepresentative",
      salesRepExpFieldName: "salesRepresentativeExpDate",
      hasExpiration: true,
    },
    {
      title: "Account Executive 2",
      salesRepFieldName: "salesRepresentative2",
      salesRepExpFieldName: "salesRepresentative2ExpDate",
      hasExpiration: true,
    },
    {
      title: "Business Developer",
      salesRepFieldName: "salesBusinessDeveloper",
      salesRepExpFieldName: "salesBusinessDeveloperExpDate",
      hasExpiration: true,
    },
    {
      title: "Direct Sales Lead",
      salesRepFieldName: "directSalesLead",
      salesRepExpFieldName: "directSalesLeadExpDate",
      hasExpiration: true,
    },
    {
      title: "Team Manager",
      salesRepFieldName: "teamManager",
      salesRepExpFieldName: "teamManagerExpDate",
      hasExpiration: true,
    },
    {
      title: "Customer Success Manager",
      salesRepFieldName: "customerSuccessManager",
      salesRepExpFieldName: "", // No expiration field for CSM
      hasExpiration: false,
    },
    {
      title: "Partner Manager",
      salesRepFieldName: "partnerManager",
      salesRepExpFieldName: "partnerManagerExpDate",
      hasExpiration: true,
    },
  ];

  const isDisabled = (salesRep: SalesRepType) => {
    if (
      currentBusiness[salesRep as keyof IBusinessFull] &&
      !isSalesSuperAdmin
    ) {
      return true;
    }
  };
  return (
    <Modal
      visible={visible}
      title={"Assign - " + currentBusiness?.businessName}
      onCancel={handleCancel}
      footer={null}
      width={650}
    >
      <Form onFinish={handleSubmit} form={form}>
        <Tabs tabPosition="left">
          {SalesRepsForms.map((salesForm) => (
            <Tabs.TabPane
              tab={
                <>
                  <SalesRepIcon
                    type={salesForm.salesRepFieldName}
                    label={salesForm.title}
                  />
                </>
              }
              key={"item-" + salesForm.salesRepFieldName}
            >
              <Form.Item
                label="Name"
                name={salesForm.salesRepFieldName}
                labelCol={salesFormFieldLayout.labelCol}
                labelAlign="left"
              >
                <Select
                  disabled={isDisabled(salesForm.salesRepFieldName)}
                  onChange={(value) =>
                    handleSelectionChange(value, salesForm.salesRepFieldName)
                  }
                  options={saleRepSelectionOptions}
                />
              </Form.Item>

              {salesForm.hasExpiration && (
                <Form.Item
                  name={salesForm.salesRepExpFieldName}
                  label="Expiration Date"
                  hidden={!isSalesSuperAdmin}
                  labelCol={salesFormFieldLayout.labelCol}
                  labelAlign="left"
                  help="Default: 12 months following first transaction date"
                >
                  <DatePicker format={dateFormat} />
                </Form.Item>
              )}
            </Tabs.TabPane>
          ))}
        </Tabs>
        <Form.Item>
          <Button
            style={{ float: "right" }}
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Update
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
